import React from 'react'
import PropTypes from 'prop-types'
import { classNames } from 'utils/utils'
import withStyle, {
  SvgStyled,
  CircleStyled,
  CountStyled,
} from './DonutFillChart.style.js'
import { START_AT_TOP, CLOCKWISE, MAX_ARC_PROPORTION } from './circleConstants'
import { getGradientType } from '../gradients'

const { sin, cos, PI, min } = Math

const DonutFillChart = ({ className, count = 0, total = 0, id }) => {
  // NOTE: An overview of the SVG math is in comments at bottom of this file

  const angleArcStart = START_AT_TOP
  const direction = CLOCKWISE
  const directionSign = direction ? 1 : -1

  const strokeWidth = 1 / 6 // as the desired proportion of circle radius
  const viewboxWidth = 2 + strokeWidth
  const negativeViewboxHalfWidth = (-1 * viewboxWidth) / 2

  const arcProportionOfCircle = min(count / total, MAX_ARC_PROPORTION) // (see comment at bottom)
  const angleSweep = arcProportionOfCircle * 2 * PI // because sin & cos take angle measures in radians
  const angleArcEnd = angleArcStart + directionSign * angleSweep
  const largeArcFlag = arcProportionOfCircle > 0.5 ? 1 : 0

  const gradient = getGradientType(arcProportionOfCircle)

  return (
    <div className={classNames(className, 'donut-chart')}>
      <SvgStyled
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`${negativeViewboxHalfWidth} ${negativeViewboxHalfWidth} ${viewboxWidth} ${viewboxWidth}`}
      >
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
            {gradient.map(({ offset, color }) => (
              <stop key={color} offset={offset} stopColor={color} />
            ))}
          </linearGradient>
        </defs>
        <CircleStyled
          fillOpacity="0"
          strokeWidth={strokeWidth}
          cx="0"
          cy="0"
          r="1"
        />
        <path
          d={`
            M ${cos(angleArcStart)} ${sin(angleArcStart)}
            A 1 1 0 ${largeArcFlag} ${direction} ${cos(angleArcEnd)} ${sin(
            angleArcEnd
          )}
          `}
          strokeWidth={strokeWidth}
          fillOpacity="0"
          stroke={`url(#${id})`}
        />
      </SvgStyled>
      <CountStyled>{count}</CountStyled>
    </div>
  )
}

DonutFillChart.defaultProps = {}

DonutFillChart.propTypes = {
  /** Make sure to comment */
  /** Classname */
  className: PropTypes.string,
  count: PropTypes.number,
  total: PropTypes.number,
  id: PropTypes.string, // to keep things unique
}

export default withStyle(DonutFillChart)

/* ----------------------------------------------------------
SVG Math

The math is done using a circle of radius of 1 (diameter 2)
centered in the viewbox at (0,0). This allows sin and cos fns
to just work for coordinates. We center everything with
the viewbox, and scaling the diagram can be done from the
outside with css.

We want a circle that perfectly fills the space of its dom elem.
Having a stroke width throws off the viewbox math for this a bit.
The arc-path's line is defined as the center of the stroke,
so to fit it all in we must account for stroke thickness
by widening the viewbox a corresponding amount beyond a radius of 1.

angleArcStart and angleArcEnd refer to the measure of one
end of the arc as the angle between: (a) the radius formed from
that point to the origin and: (b) the radius from (1,0) to the origin,
with positive angle denoting the clockwise direction (unlike
traditinal trig. unit circles, which depict +angles counter clock-
wise, because in SVGs the y-axis is inverted -- points down, not up.)

To draw a full circle with an arc path, we actually draw a circle
that is just barely imcomplete. Otherwise, the start-point and
end-point will be the same and the arc will draw as a single point.
So the arc proportion is constrained (MAX_ARC_PROPORTION)
to no more than .999
*/
